<template>
    <CRow>
        <CCol>
            <CCard>
                <CCardHeader>
                    <CRow>
                        <CCol><h5>User Management</h5> </CCol>
                        <CCol>
                            <CButton
                                color="primary"
                                class="m-2 float-right"
                                @click="
                                    addUserModal = true;
                                    isEmailExist = false;
                                    reset();
                                "
                                >Add User
                            </CButton>
                        </CCol>
                    </CRow>
                </CCardHeader>
                <CCardBody :style="{ position: 'relative', top: '-18px' }">
                    <CDataTable
                        :items="users"
                        :fields="fields"
                        :column-filter="false"
                        :itemsPerPageSelect="true"
                        hover
                        sorter
                        pagination
                        @filtered-items-change="setCurrentItems"
                    >
                        <template #account_status="{ item }">
                            <td
                                class="font-weight-bold"
                                :style="`color: ${getColorOnStatus(
                                    getAccountStatus(item)
                                )}`"
                            >
                                {{ getAccountStatus(item) }}
                            </td>
                        </template>
                        <template #client_name="{ item }">
                            <td>{{ item.client.name }}</td>
                        </template>
                        <template #role_name="{ item }">
                            <td>
                                <CBadge
                                    color="primary"
                                    class="font-weight-bold"
                                    shape="pill"
                                    style="font-size: 90%"
                                >
                                    {{ getFullRoleName(item.role.name) }}
                                </CBadge>
                            </td>
                        </template>
                        <template #action="{ item }">
                            <!-- Action buttons -->
                            <template v-if="renderActions">
                                <CButton
                                    v-if="item.verification_pending === false"
                                    color="success"
                                    square
                                    size="sm"
                                    class="m-1"
                                    v-c-tooltip="{
                                        content: 'Edit',
                                        placement: 'bottom',
                                    }"
                                    @click="
                                        addUserModal = true;
                                        edit = true;
                                        userDetails = { ...item };
                                    "
                                >
                                    <CIcon name="cil-pencil" />
                                </CButton>

                                <CButton
                                    v-if="
                                        item.verification_pending === false &&
                                        item.role.name !== 'client_admin' &&
                                        item.account_disabled === true
                                    "
                                    color="info"
                                    square
                                    size="sm"
                                    class="m-1"
                                    v-c-tooltip="{
                                        content: 'Activate',
                                        placement: 'bottom',
                                    }"
                                    @click="
                                        userDetails = { ...item };
                                        confirm.enable = true;
                                        confirm.type = 'activate';
                                    "
                                >
                                    <CIcon name="cil-file" />
                                </CButton>

                                <CButton
                                    v-if="
                                        item.verification_pending === false &&
                                        item.role.name !== 'client_admin' &&
                                        item.account_disabled === false
                                    "
                                    color="warning"
                                    square
                                    size="sm"
                                    class="m-1"
                                    v-c-tooltip="{
                                        content: 'Deactivate',
                                        placement: 'bottom',
                                    }"
                                    @click="
                                        userDetails = { ...item };
                                        confirm.enable = true;
                                        confirm.type = 'deactivate';
                                    "
                                >
                                    <CIcon name="cil-file" />
                                </CButton>

                                <CButton
                                    v-if="
                                        item.verification_pending === false &&
                                        item.role.name !== 'client_admin'
                                    "
                                    color="danger"
                                    square
                                    size="sm"
                                    class="m-1"
                                    v-c-tooltip="{
                                        content: 'Delete',
                                        placement: 'bottom',
                                    }"
                                    @click="
                                        confirm.enable = true;
                                        confirm.type = 'delete';
                                        userDetails = { ...item };
                                    "
                                >
                                    <CIcon name="cil-x-circle" />
                                </CButton>
                                <!--  -->
                            </template>
                        </template>
                    </CDataTable>
                </CCardBody>
            </CCard>
        </CCol>
        <CModal
            :title="`${edit ? 'Update' : 'Add'} User`"
            :show.sync="addUserModal"
            fade
            class="disable-footer"
            centered
            :closeOnBackdrop="false"
        >
            <CForm>
                <CInput
                    label="Name"
                    horizontal
                    :value.sync="$v.userDetails.name.$model"
                    :isValid="checkIfValidUser('name')"
                    placeholder="Enter the name"
                    :invalidFeedback="`Enter a valid user name between 3 and 50 characters. \nNumbers and special characters not allowed`"
                />
                <CInput
                    label="Email"
                    horizontal
                    :value.sync="$v.userDetails.email.$model"
                    :isValid="checkIfValidUser('email')"
                    :disabled="edit"
                    placeholder="Enter the email"
                    :invalidFeedback="
                        isEmailExist
                            ? 'Email already exist'
                            : 'Enter a valid email. Max 50 characters'
                    "
                    @input="isEmailExist = false"
                />
            </CForm>
            <div class="inactive-modal-response mt-4 float-right">
                <CButton
                    type="cancel"
                    color="secondary"
                    class="m-1"
                    @click="
                        addUserModal = false;
                        reset();
                    "
                >
                    Cancel
                </CButton>
                <CButton
                    type="submit"
                    color="primary"
                    class="m-1"
                    @click="edit ? updateUser() : addUser()"
                >
                    Submit
                </CButton>
            </div>
        </CModal>

        <Confirm
            :handleOperation="
                confirm.type === 'delete' ? deleteUser : changeUserStatus
            "
            :modal="confirm.enable"
            :type="confirm.type"
            @close="confirm.enable = false"
        />
    </CRow>
</template>

<script>
import gql from "graphql-tag";
import { storeData } from "../../store";
import { validationMixin } from "vuelidate";
import {
    required,
    minLength,
    maxLength,
    helpers,
    email,
} from "vuelidate/lib/validators";
import _ from "../../utility/utility";
import queries from "./queries";

const fields = [
    { key: "name", label: "Name" },
    { key: "email" },
    { key: "client_name", label: "Client / Hospital Name" },
    { key: "role_name", label: "Role" },
    { key: "account_status", label: "Account Status" },
    { key: "action", label: "Action", _classes: "remove-sort-arrow" },
];

export default {
    data() {
        return {
            currentItems: [],
            users: [],
            currentUser: {},
            fields,
            eachUserModal: false,
            userApprovalComment: "",
            userDetails: {
                name: "",
                email: "",
            },
            addUserModal: false,

            edit: false,
            confirm: { enable: false, type: "" },
            renderActions: true,
            isEmailExist: false,
        };
    },
    mixins: [validationMixin],
    methods: {
        checkIfValidUser(fieldName) {
            let field = this.$v.userDetails[fieldName];
            if (!field.$dirty) {
                return null;
            }
            return !(field.$invalid || field.$model === "");
        },
        // Based on status
        isActive: (user) =>
            !user.verification_pending &&
            !user.is_rejected &&
            !user.account_disabled,

        isDisabled: (user) => user.account_disabled,
        isVerificationPending: (user) => user.verification_pending,
        isRejected: (user) => user.is_rejected,

        // Based on role
        isSysAdmin: (user) => user.role.name === "sys_admin",
        isGroupAdmin: (user) => user.role.name === "group_admin",
        isClientUser: (user) => user.role.name === "client_user",
        isClientAdmin: (user) => user.role.name === "client_admin",

        getFullRoleName: (roleName) => {
            if (roleName === "client_admin") return "Client Admin";
            else if (roleName === "client_user") return "Client User";
            else if (roleName === "sys_admin") return "System Admin";
            else if (roleName === "group_admin") return "Group Admin";
            else return "";
        },
        getAccountStatus(item) {
            if (this.isActive(item)) return "Active";
            else if (this.isDisabled(item)) return "Inactive";
            else if (this.isRejected(item)) return "Rejected";
            else if (this.isVerificationPending(item)) return "Pending";
            else return "";
        },

        getColorOnStatus(status) {
            if (status === "Active") return "#0bbd40";
            else if (status === "Disabled") return "#ff6700";
            else if (status === "Pending") return "#b6bccb";
            else return "#f11a1a";
        },

        setCurrentItems(val) {
            this.currentItems = val;
        },
        reset() {
            this.$v.$reset();
            this.userDetails = {
                name: "",
                email: "",
            };
            this.edit = false;
        },
        async addUser() {
            try {
                this.$v.$touch();
                if (!this.$v.$invalid) {
                    const res = await this.$apollo.mutate({
                        mutation: queries.INSERT_USER,
                        variables: {
                            ...this.userDetails,
                            client_id: storeData.currentUserDetails.client_id,
                            added: _.getCurrentTimeStamp(),
                            updated: _.getCurrentTimeStamp(),
                        },
                    });
                    const result = res.data.insert_spendmd_db_users.returning;
                    if (result[0].id) {
                        this.$apollo.queries.userData.refetch();
                        this.addUserModal = false;
                        this.reset();
                    }
                }
            } catch (err) {
                if (
                    err.message.includes(
                        'duplicate key value violates unique constraint "users_email_key"'
                    )
                ) {
                    this.isEmailExist = true;
                    this.$v.userDetails.email.$touch();
                }
            }
        },
        async updateUser() {
            try {
                this.$v.userDetails.name.$touch();
                if (!this.$v.userDetails.name.$invalid) {
                    const res = await this.$apollo.mutate({
                        mutation: queries.UPDATE_USER,
                        variables: {
                            id: this.userDetails.id,
                            name: this.userDetails.name,
                            account_disabled: this.userDetails.account_disabled,
                            updated: _.getCurrentTimeStamp(),
                        },
                    });
                    const result = res.data.update_spendmd_db_users.returning;
                    if (result[0].id) {
                        this.$apollo.queries.userData.refetch();
                        this.addUserModal = false;
                        this.reset();
                    }
                }
            } catch (err) {
                console.log(err);
            }
        },
        async changeUserStatus() {
            try {
                const res = await this.$apollo.mutate({
                    mutation: queries.CHANGE_USER_STATUS,
                    variables: {
                        id: this.userDetails.id,
                        account_disabled:
                            this.confirm.type === "deactivate" ? true : false,
                        updated: _.getCurrentTimeStamp(),
                    },
                });
                const result = res.data.update_spendmd_db_users.returning;
                if (result[0].id) {
                    this.renderActions = false;
                    this.reset();
                    this.confirm.enable = false;
                    this.$apollo.queries.userData.refetch();
                    setTimeout(() => {
                        this.renderActions = true;
                    }, 800);
                }
            } catch (err) {
                console.log(err);
            }
        },
        async deleteUser() {
            try {
                const res = await this.$apollo.mutate({
                    mutation: queries.DELETE_USER,
                    variables: {
                        id: this.userDetails.id,
                    },
                });
                const result = res.data.delete_spendmd_db_users.returning;
                if (result[0].id) {
                    this.confirm.enable = false;
                    this.$apollo.queries.userData.refetch();
                    this.reset();
                }
            } catch (err) {
                console.log(err);
            }
        },
    },
    validations: {
        userDetails: {
            name: {
                required,
                minLength: minLength(3),
                maxLength: maxLength(50),
                isValid: helpers.regex(
                    "isAddress1Valid",
                    /^[A-Za-z-_]+(?: +[A-Za-z-_]+)*$/i
                ),
            },
            email: {
                required,
                email,
                maxLength: maxLength(50),
                isEmailExist() {
                    if (this.isEmailExist) return false;
                    this.isEmailExist = false;
                    if (
                        this.users.filter(
                            (el) =>
                                el.email.toLowerCase() ===
                                this.userDetails.email.toLowerCase()
                        ).length > 0
                    ) {
                        this.isEmailExist = true;
                    }
                    if (this.isEmailExist) return false;
                    return true;
                },
            },
        },
    },
    apollo: {
        userData: {
            query: queries.GET_USER_DETAILS,
            update: (data) => data,
            result({ data }) {
                this.users = data.spendmd_db_users;
            },
        },
    },
};
</script>

<style scoped>
textarea.form-control {
    height: 112px !important;
}
</style>
